import Banner from "../layout/Banner";
import { Footer } from "../landing/Footer";
import TopNavbar from "../landing/TopNavbar";
import {
  AdjustmentsHorizontalIcon,
  ArrowUpTrayIcon,
  ArrowsPointingOutIcon,
  ArrowsRightLeftIcon,
  BellAlertIcon,
  BookOpenIcon,
  ChartPieIcon,
  Cog8ToothIcon,
  CreditCardIcon,
  CursorArrowRaysIcon,
  EyeIcon,
  HeartIcon,
  ListBulletIcon,
  MicrophoneIcon,
  PhotoIcon,
  ShoppingCartIcon,
  SquaresPlusIcon,
  TableCellsIcon,
  TagIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/solid";
import Feature from "./Feature";
import CallToAction from "../layout/CallToAction";
import getButtonStyle from "../../utils/styles";

const features = [
  {
    title: "Dashboard",
    subtitle: "Real time updates",
    description:
      "Be notified in real time when actionable requests are made and action them instantly.",
    features: [
      {
        name: "Real time updates.",
        description:
          "No batch processes, receive updates as they happen so you can act in the moment.",
        icon: BellAlertIcon,
      },
      {
        name: "Intelligent categorisation.",
        description:
          "Our AI language models process requests and categorise them to be dealt with.",
        icon: AdjustmentsHorizontalIcon,
      },
      {
        name: "Immediate action.",
        description:
          "The right teams can receive updates how they want them and action them with ease.",
        icon: CursorArrowRaysIcon,
      },
    ],
    image: "/img/graphics/screenshots/feed.svg",
    action: {
      text: "Interested in receiving real time updates?",
      url: "https://audico.co.uk/contact",
      subtext: "Send us an inquiry",
    },
  },
  {
    title: "Payments",
    subtitle: "Seamless payment experience with Pay-by-Voice",
    description:
      "Complete transactions, check-out and fulfill purchases with ease.",
    features: [
      {
        name: "Build & pay for your basket.",
        description:
          "Create a basket of items and pay for them with a simple voice command.",
        icon: ShoppingCartIcon,
      },
      {
        name: "Trusted & fully compliant payment processing.",
        description:
          "We partner with some of the biggest payment providers globally, such as Elavon, to ensure your payments are processed securely.",
        icon: CreditCardIcon,
      },
      {
        name: "Instant receipt and notification.",
        description:
          "When a payment is complete, the consumer can receive an email receipt and the property will be notified immediately.",
        icon: BellAlertIcon,
      },
    ],
    image: "/img/graphics/screenshots/payments.svg",
    action: {
      text: "Want to increase your revenue?",
      url: "https://audico.co.uk/contact",
      subtext: "Let us show you how",
    },
  },
  {
    title: "Administration",
    subtitle: "Monitor your fleet",
    description:
      "Get a global view of all active & inactive rooms and devices.",
    features: [
      {
        name: "Personalisation.",
        description:
          "Tailor the voice and touch experience for each room based on your or their requirements.",
        icon: HeartIcon,
      },
      {
        name: "Control.",
        description:
          "Easily update and configure settings across your fleet of devices or rooms as an administrator.",
        icon: Cog8ToothIcon,
      },
      {
        name: "Monitor.",
        description:
          "Drill down into granular details or monitor your fleet of devices and rooms at a high level.",
        icon: TableCellsIcon,
      },
    ],
    image: "/img/graphics/screenshots/rooms.svg",
    action: {
      text: "Want oversight of your smart device fleet?",
      url: "https://audico.co.uk/contact",
      subtext: "Get in touch",
    },
  },
  {
    title: "Multi-modal",
    subtitle: "Design engaging visual and audio experiences",
    description:
      "Design your desired voice experience and control the visual experience with ease.",
    features: [
      {
        name: "Voice experience.",
        description:
          "Whatever your requirements, our team of experts will help you to design a compelling voice experience and roadmap.",
        icon: MicrophoneIcon,
      },
      {
        name: "Visual assets.",
        description:
          "Quickly create personalised visual assets to be displayed in rooms on devices, such as reminders, updates & upcoming bookings.",
        icon: PhotoIcon,
      },
      {
        name: "Personalisation.",
        description:
          "Deliver tailored offers, promotions and recommendations to guests to enhance their experience, boost revenue and increase loyalty.",
        icon: UserGroupIcon,
      },
    ],
    image: "/img/graphics/screenshots/campaigns.svg",
    action: {
      text: "Are you looking to engage with your guests?",
      url: "https://audico.co.uk/contact",
      subtext: "Reach out to us",
    },
  },
  {
    title: "Insights",
    subtitle: "Actionable insights on demand",
    description:
      "Improve operations and understanding of your operations with actionable insights.",
    features: [
      {
        name: "Reporting.",
        description:
          "Generate insight reports for a chosen time window within a few clicks.",
        icon: ChartPieIcon,
      },
      {
        name: "Export.",
        description:
          "Export your data to format that suits you for further data mining.",
        icon: ArrowUpTrayIcon,
      },
      {
        name: "Oversight.",
        description:
          "If you manage multiple properties gain access to a view with oversight across your estate.",
        icon: EyeIcon,
      },
    ],
    image: "/img/graphics/screenshots/analytics.svg",
    action: {
      text: "Want to see a demo?",
      url: "https://audico.co.uk/contact",
      subtext: "Book some time with our experts",
    },
  },
  {
    title: "Audit",
    subtitle: "Accountability and traceability built-in",
    description:
      "Don't rely on a paper trail, visually see the audit trail of actions taken and by whom.",
    features: [
      {
        name: "Log trace.",
        description:
          "Access full logs of all actions going through the system and by whom they were completed.",
        icon: ListBulletIcon,
      },
      {
        name: "Export.",
        description:
          "Export your data to format that suits you for further investigation.",
        icon: ArrowUpTrayIcon,
      },
      {
        name: "Tagging.",
        description:
          "Visually scan your logs and find what you are looking for with intelligent tags for each log.",
        icon: TagIcon,
      },
    ],
    image: "/img/graphics/screenshots/log.svg",
    action: {
      text: "Looking to digitise your log trail?",
      url: "https://audico.co.uk/contact",
      subtext: "Send us a message",
    },
  },
  {
    title: "Integration",
    subtitle: "Fully integrate with your existing systems",
    description:
      "Our platform is interoperable and open for integration into any of your systems.",
    features: [
      {
        name: "Push or pull.",
        description:
          "Push audico data into your systems or pull third party data in audico, we make it simple to do both.",
        icon: ArrowsRightLeftIcon,
      },
      {
        name: "Bring your own data.",
        description:
          "We can ingest any data you have and make it available to you in our platform.",
        icon: TableCellsIcon,
      },
      {
        name: "Customisation.",
        description:
          "If you need something more custom, we can accomodate to your business needs.",
        icon: WrenchScrewdriverIcon,
      },
    ],
    image: "/img/graphics/screenshots/integration.svg",
    action: {
      text: "Looking for an intergrated platform?",
      url: "https://audico.co.uk/contact",
      subtext: "Send us a message",
    },
  },
  {
    title: "Content",
    subtitle: "Personalised content management",
    description:
      "Manage events, services, menus, newsletters, surveys and more on mass.",
    features: [
      {
        name: "Customisation.",
        description:
          "Create content to reflect the available activities & services that are available in your property.",
        icon: SquaresPlusIcon,
      },
      {
        name: "Experience.",
        description:
          "Build a library of content to create delightful experiences and to make their visit even more memorable.",
        icon: BookOpenIcon,
      },
      {
        name: "Scale.",
        description:
          "Quickly and easily create, update and delete content for specific rooms or entire fleets.",
        icon: ArrowsPointingOutIcon,
      },
    ],
    image: "/img/graphics/screenshots/surveys.svg",
    action: {
      text: "Want to stimulate residents?",
      url: "https://audico.co.uk/contact",
      subtext: "Speak with our team",
    },
  },
];

export default function Features() {
  return (
    <div className="bg-midnight">
      <TopNavbar />

      {/* Features */}
      {features.map((x, idx) => (
        <Feature
          key={idx}
          // type={idx % 2 === 0 ? "left" : "right"}
          type="left"
          data={x}
          last={idx === features.length - 1}
        />
      ))}

      {/* Call to action */}
      <CallToAction bgColour="bg-midnight">
        <div className="mx-auto max-w-xl lg:max-w-3xl text-center">
          <h2 className="font-display tracking-tight text-primarygotham-bold text-3xl sm:text-4xl md:text-5xl gotham-bold text-primary">
            Are you ready to get started?
          </h2>
          <p className="mt-4 text-lg tracking-tight text-slate-200">
            We have a team of experts ready to help you and many more features
            that we can share over a discovery call. Get in touch to find out
            more!
          </p>
          <a
            className={`${getButtonStyle("solid", "info")} mt-10`}
            href="https://audico.co.uk/contact"
            target="_blank"
            rel="noreferrer"
          >
            I want to find out more
          </a>
        </div>
      </CallToAction>

      {/* Footer */}
      <Footer />

      {/* Alert banner */}
      <Banner />
    </div>
  );
}
